import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Check, Loader2, Eye } from "lucide-react";

const FORMATS = ["quiz", "truefalse", "guess", "catch", "funfact", "favorite"];
const FORMAT_LABEL = {
  quiz: "Pop Quiz", truefalse: "True or False", guess: "Take a Guess",
  catch: "Did You Catch It?", funfact: "Fun Fact", favorite: "Pick Your Favorite",
};

const SLOTS = [
  { key: "slot0", title: "Fun Fact Card 1", when: "After about the 4th step" },
  { key: "slot1", title: "Fun Fact Card 2", when: "After about the 8th step" },
  { key: "slot2", title: "Fun Fact Card 3", when: "After about the 12th step" },
];

// One switch + (when ON) a live preview the creator can cycle & lock in.
const CardSlot = ({ manualId, orientation, slotIndex, enabled, locked, onToggle, onLock, getExclude, reportCard }) => {
  const [busy, setBusy] = useState(false);
  const [card, setCard] = useState(null); // { format, question, options, answer_index, has_reveal, image, answer_image }
  const [showAnswer, setShowAnswer] = useState(false);
  const [savedNow, setSavedNow] = useState(!!(locked && locked.question));
  const fmtIdx = useRef(slotIndex % FORMATS.length);  // start each slot on a different format
  const loadedOnce = useRef(false);
  const meta = SLOTS[slotIndex];

  const fetchPreview = useCallback(async (body) => {
    setBusy(true);
    setShowAnswer(false);
    try {
      const exclude = getExclude ? getExclude(slotIndex) : [];
      const { data } = await api.post(`/manuals/${manualId}/retention-card/preview`, { orientation, exclude, ...body });
      setCard(data);
      reportCard?.(slotIndex, data?.question);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't build a card");
      return null;
    } finally {
      setBusy(false);
    }
  }, [manualId, orientation, slotIndex, getExclude, reportCard]);

  // First time this slot is switched ON: restore the locked card, else generate a fresh first one.
  // When switched OFF, reset so re-enabling later fetches a fresh preview again.
  useEffect(() => {
    if (!enabled) {
      loadedOnce.current = false;
      setCard(null);
      setShowAnswer(false);
      reportCard?.(slotIndex, null);
      return;
    }
    if (loadedOnce.current) return;
    loadedOnce.current = true;
    if (locked && locked.question) {
      const i = FORMATS.indexOf(locked.format);
      if (i >= 0) fmtIdx.current = i;
      reportCard?.(slotIndex, locked.question);
      fetchPreview({ format: locked.format, question: locked.question, options: locked.options, answer_index: locked.answer_index, answer_text: locked.answer_text })
        .then((d) => { if (d) setSavedNow(true); });
    } else {
      setSavedNow(false);
      fetchPreview({ format: FORMATS[fmtIdx.current] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const nextCard = () => {
    fmtIdx.current = (fmtIdx.current + 1) % FORMATS.length;
    setSavedNow(false);
    fetchPreview({ format: FORMATS[fmtIdx.current] });
  };

  const useThisCard = () => {
    if (!card) return;
    onLock({ format: card.format, question: card.question, options: card.options, answer_index: card.answer_index, answer_text: card.answer_text });
    setSavedNow(true);
    toast.success(`${meta.title} locked in ✓`);
  };

  return (
    <div data-testid={`retention-slot-${slotIndex}`} className="mb-4">
      {/* Switch */}
      <button
        type="button"
        data-testid={`retention-toggle-${slotIndex}`}
        onClick={() => onToggle(!enabled)}
        className="w-full flex items-center justify-between gap-3 rounded-2xl border-2 px-4 py-3.5 transition-colors"
        style={enabled
          ? { borderColor: "#7fbf82", background: "linear-gradient(135deg, rgba(143,191,130,.24), rgba(93,138,82,.14))", boxShadow: "0 0 0 1px rgba(127,191,130,.25)" }
          : { borderColor: "#7c5cff", background: "linear-gradient(135deg, #2b2545, #211c33)", boxShadow: "0 8px 22px -14px rgba(124,92,255,.9)" }}
      >
        <span className="flex items-center gap-2.5 text-left">
          <span className="flex items-center justify-center w-8 h-8 rounded-xl shrink-0"
            style={{ background: enabled ? "rgba(127,191,130,.22)" : "rgba(124,92,255,.20)" }}>
            <Sparkles size={17} style={{ color: enabled ? "#a6e59a" : "#f5c518" }} />
          </span>
          <span className="leading-tight">
            <span className="block font-extrabold text-[15px]" style={{ color: "#ffffff" }}>{meta.title}</span>
            <span className="block text-[11px] font-semibold" style={{ color: enabled ? "#a6e59a" : "#c3b9ff" }}>
              {meta.when}
            </span>
          </span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] font-extrabold tracking-wide" style={{ color: enabled ? "#a6e59a" : "#8f88ad" }}>
            {enabled ? "ON" : "OFF"}
          </span>
          <span className="relative inline-block w-11 h-6 rounded-full transition-colors" style={{ background: enabled ? "#4f9e46" : "#4a4560" }}>
            <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all" style={{ left: enabled ? "22px" : "2px" }} />
          </span>
        </span>
      </button>

      {enabled && (
        <div className="rounded-2xl border-2 p-4 mt-2" style={{ borderColor: "#5d8a52", background: "#0f0f16" }} data-testid={`retention-preview-wrap-${slotIndex}`}>
          <div
            className="relative w-full mx-auto rounded-xl overflow-hidden bg-[#0f0f16] flex items-center justify-center"
            style={{ aspectRatio: orientation === "landscape" ? "16 / 9" : "9 / 16", maxWidth: orientation === "landscape" ? 460 : 236 }}
          >
            {busy && (
              <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/40">
                <Loader2 className="animate-spin text-white" size={26} />
              </div>
            )}
            {card && (
              <img
                data-testid={`retention-preview-img-${slotIndex}`}
                src={showAnswer && card.answer_image ? card.answer_image : card.image}
                alt="Fun fact card preview"
                className="w-full h-full object-cover"
              />
            )}
          </div>

          <div className="flex items-center justify-between mt-3">
            <span data-testid={`retention-format-label-${slotIndex}`} className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ background: "#1e3aaa" }}>
              {card ? (FORMAT_LABEL[card.format] || card.format) : "…"}
            </span>
            {card?.has_reveal && (
              <button
                type="button"
                data-testid={`retention-peek-answer-${slotIndex}`}
                onClick={() => setShowAnswer((v) => !v)}
                className="flex items-center gap-1.5 text-xs font-bold text-[#4f9e46] hover:underline"
              >
                <Eye size={14} /> {showAnswer ? "Show question" : "Peek at answer"}
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3">
            <button
              type="button"
              data-testid={`retention-next-btn-${slotIndex}`}
              onClick={nextCard}
              disabled={busy}
              className="flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-2.5 font-bold text-sm transition-colors disabled:opacity-50"
              style={{ borderColor: "#1e3aaa", color: "#ffffff", background: "#1e3aaa" }}
            >
              <RefreshCw size={16} /> Show me another
            </button>
            <button
              type="button"
              data-testid={`retention-use-btn-${slotIndex}`}
              onClick={useThisCard}
              disabled={busy || !card || savedNow}
              className="flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 font-bold text-sm transition-colors disabled:opacity-60"
              style={savedNow ? { background: "#1e6fff", color: "#fff" } : { background: "#e11d2a", color: "#fff" }}
            >
              <Check size={16} /> {savedNow ? "Locked in" : "Use this card"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const RetentionCardStudio = ({ manualId, orientation = "landscape", initial }) => {
  const [slots, setSlots] = useState(() => SLOTS.map((s) => {
    const src = (initial && initial[s.key]) || {};
    return {
      enabled: !!src.enabled,
      locked: src.question ? { format: src.format, question: src.question, options: src.options, answer_index: src.answer_index, answer_text: src.answer_text } : null,
    };
  }));

  // Shared registry of the question each slot is currently showing/locked — so no two cards repeat.
  const questionsRef = useRef(null);
  if (questionsRef.current === null) {
    questionsRef.current = {};
    SLOTS.forEach((s, i) => { const q = initial && initial[s.key] && initial[s.key].question; if (q) questionsRef.current[i] = q; });
  }
  const reportCard = useCallback((i, q) => {
    if (q) questionsRef.current[i] = q; else delete questionsRef.current[i];
  }, []);
  const getExclude = useCallback((i) => Object.entries(questionsRef.current)
    .filter(([k]) => Number(k) !== i).map(([, v]) => v).filter(Boolean), []);

  const persist = useCallback(async (next) => {
    const body = {};
    SLOTS.forEach((s, i) => {
      const st = next[i];
      body[s.key] = {
        enabled: !!st.enabled,
        format: st.locked?.format ?? null,
        question: st.locked?.question ?? "",
        options: st.locked?.options ?? [],
        answer_index: st.locked?.answer_index ?? null,
        answer_text: st.locked?.answer_text ?? "",
      };
    });
    try { await api.post(`/manuals/${manualId}/retention`, body); }
    catch { toast.error("Couldn't save your Fun Fact card settings — please try again"); }
  }, [manualId]);

  const update = (i, patch) => {
    setSlots((prev) => {
      const next = prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
      persist(next);
      return next;
    });
  };

  const anyOn = slots.some((s) => s.enabled);

  return (
    <div data-testid="retention-studio">
      <p className="text-[11px] mb-3 px-1 leading-relaxed" style={{ color: "#a8a4ba" }}>
        Drop a full-screen <b style={{ color: "#d9d5ea" }}>Fun Fact card</b> into your video to stop the scroll and re-hook viewers.
        Each card shows a question for 7s, then reveals the answer for 3s. All three are <b style={{ color: "#d9d5ea" }}>off by default</b> — flip on the ones you want. They land roughly after your 4th, 8th and 12th steps; longer videos fit more cards.
      </p>

      {SLOTS.map((s, i) => (
        <CardSlot
          key={s.key}
          manualId={manualId}
          orientation={orientation}
          slotIndex={i}
          enabled={slots[i].enabled}
          locked={slots[i].locked}
          onToggle={(val) => update(i, { enabled: val })}
          onLock={(cardObj) => update(i, { locked: cardObj })}
          getExclude={getExclude}
          reportCard={reportCard}
        />
      ))}

      {!anyOn && (
        <p className="text-[11px] text-center" style={{ color: "#8f88ad" }} data-testid="retention-none-note">
          No Fun Fact cards will be added to this video.
        </p>
      )}
    </div>
  );
};

export default RetentionCardStudio;
